import React from 'react';

import { func, number, shape } from 'prop-types';
import * as R from 'ramda';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import { Link as RouterLink } from 'react-router-dom';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { useGetGlobalCustomersQuery } from 'feature/subscription-plans/enrollment/enrollmentEndpoints';
import TerminalCardSkeleton from './TerminalCard.skeleton';
import TerminalStatusChip from '../TerminalStatusChip';
import RightNow from '../RightNow';

const styles = {
  additiveChip: {
    backgroundColor: 'surface.white',
    borderColor: 'graphic.light',
    borderStyle: 'solid',
    borderWidth: '1px',
    color: 'success.main',
  },
  chips: {
    '.MuiChip-root': {
      fontSize: '12px',
      height: 'unset',
      mb: 0.5,
      mr: 0.5,
      px: 0,
      py: 0.25,
    },
    ml: 0,
    mr: 0.5,
    my: 0.5,
  },
  container: {
    '> *': {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    backgroundColor: 'surface.light',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderColor: 'graphic.light',
    display: 'flex',
    flexDirection: 'row',
    pb: 1.5,
    pt: 0,
    px: 1.5,
  },
  productChip: {
    backgroundColor: 'primary.light',
    borderWidth: '1px',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    mt: 1.5,
    position: 'relative',
  },
  terminalHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  terminalName: {
    alignSelf: 'flex-start',
    justifyContent: 'flex-start',
    ml: -1.5,
    px: 1.5,
    width: 'fit-content',
  },
  terminalNameBadge: {
    '.MuiBadge-badge': {
      backgroundColor: 'error.dark',
      right: -10,
      top: '10%',
    },
  },
};

const TerminalCard = ({ location, setSelectedTerminal, terminal, sx, ...props }) => {
  const { data: globalCustomersQuery = [] } = useGetGlobalCustomersQuery();
  const hasAffiliateWithSubscription = globalCustomersQuery.some(affiliate =>
    R.equals(affiliate.hasActiveSubscription, true)
  );

  const hasCoordinates = R.not(R.isNil(location?.latitude)) && R.not(R.isNil(location?.longitude));
  const terminalDetailsPath = terminal
    ? `./${terminal.terminalId}${
        hasCoordinates
          ? `?latitude=${encodeURIComponent(location.latitude)}&longitude=${encodeURIComponent(
              location.longitude
            )}`
          : ''
      }`
    : undefined;

  const handleFocusTerminal = terminalId => {
    setSelectedTerminal(null);
    setTimeout(() => setSelectedTerminal(terminalId), 0);
  };

  return (
    <Box sx={{ ...styles.container, ...sx }} {...props}>
      {terminal ? (
        <>
          <Box>
            <Box sx={styles.row}>
              <Box sx={styles.terminalHeader}>
                <Button
                  component={RouterLink}
                  onClick={() => setSelectedTerminal(terminal.terminalId)}
                  sx={styles.terminalName}
                  to={terminalDetailsPath}
                  variant="text"
                >
                  <Badge
                    invisible={!terminal.alertCount}
                    sx={styles.terminalNameBadge}
                    variant="dot"
                  >
                    <Typography
                      sx={{ cursor: 'pointer', fontSize: '18px', textDecoration: 'underline' }}
                    >
                      {terminal.terminalIdName}
                    </Typography>
                  </Badge>
                </Button>
                <IconButton
                  aria-label={`Focus ${terminal.terminalIdName} on map`}
                  color="primary"
                  onClick={() => handleFocusTerminal(terminal.terminalId)}
                  size="small"
                >
                  <LocationOnRoundedIcon fontSize="medium" />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: '14px' }}>{terminal.displayDistance}</Typography>
            </Box>
            {hasAffiliateWithSubscription && (
              <Box sx={styles.row}>
                <Box>
                  <RightNow />
                  <TerminalStatusChip status={terminal.currentBusynessLevel ?? 'Unknown'} />{' '}
                </Box>
              </Box>
            )}
            <Box sx={styles.row}>
              <Typography sx={{ fontSize: '12px' }}>Base Products</Typography>
              <Box sx={styles.chips}>
                {terminal.products?.map(({ productId, productIdn }) => (
                  <Chip key={productId} label={productIdn} sx={styles.productChip} />
                ))}
              </Box>
            </Box>
            <Box sx={styles.row}>
              <Typography sx={{ fontSize: '12px' }}>Additives</Typography>
              <Box sx={styles.chips}>
                {terminal.additives?.map(({ additiveId, additiveName }) => (
                  <Chip key={additiveId} label={additiveName} sx={styles.additiveChip} />
                ))}
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <TerminalCardSkeleton />
      )}
    </Box>
  );
};

TerminalCard.propTypes = {
  alertCount: number,
  location: shape({
    latitude: number,
    longitude: number,
  }),
  setSelectedTerminal: func,
  sx: shape({}),
  terminal: shape({}),
};

TerminalCard.defaultProps = {
  alertCount: 0,
  location: null,
  setSelectedTerminal: undefined,
  sx: null,
  terminal: null,
};

export default TerminalCard;
